import React from 'react';

/**
 * PlanCard Component
 * Displays a subscription plan with features and pricing
 */
const PlanCard = ({
	plan,
	billingInterval = 'monthly',
	isCurrentPlan = false,
	isExactMatch = false,
	isLoading = false,
	onSelect,
	className = ''
}) => {
	const { name, description, features, priceIds } = plan;

	// Get price ID for the selected billing interval
	const priceId = priceIds[billingInterval];

	// Format feature limits
	const formatLimit = (limit) => {
		if (limit === -1) return 'Unlimited';
		return limit;
	};

	// Format export formats
	const formatExportFormats = (formats) => {
		return formats.join(', ');
	};

	const handleSelect = () => {
		if (onSelect && priceId) {
			onSelect(priceId, name);
		}
	};

	return (
		<div
			className={`relative rounded-xl border-2 p-6 transition-all ${
				isExactMatch
					? 'border-textGreen bg-lightGreen shadow-lg'
					: isCurrentPlan
					? 'border-purple bg-lightPurple shadow-lg'
					: 'border-lightGrey bg-white hover:border-gray-300 hover:shadow-md'
			} ${className}`}
		>
			{isExactMatch && (
				<div className="absolute -top-3 left-1/2 -translate-x-1/2">
					<span className="rounded-full bg-textGreen px-3 py-1 text-xs font-semibold text-white">
						✓ Active Plan
					</span>
				</div>
			)}
			{isCurrentPlan && !isExactMatch && (
				<div className="absolute -top-3 left-1/2 -translate-x-1/2">
					<span className="rounded-full bg-purple px-3 py-1 text-xs font-semibold text-white">
						Your Tier
					</span>
				</div>
			)}

			<div className="mb-4">
				<h3 className="text-2xl font-bold text-dark-blue">{name}</h3>
				<p className="mt-2 text-sm text-gray">{description}</p>
			</div>

			<div className="mb-6 space-y-3">
				<div className="flex items-center text-sm">
					<span className="font-medium text-dark-blue">Documents:</span>
					<span className="ml-2 text-gray">
						{formatLimit(features.maxDocuments)}
					</span>
				</div>

				<div className="flex items-center text-sm">
					<span className="font-medium text-dark-blue">Citations per Document:</span>
					<span className="ml-2 text-gray">
						{formatLimit(features.maxCitationsPerDocument)}
					</span>
				</div>

				<div className="flex items-center text-sm">
					<span className="font-medium text-dark-blue">Export Formats:</span>
					<span className="ml-2 text-gray">
						{formatExportFormats(features.allowedExportFormats)}
					</span>
				</div>

				<div className="flex items-center text-sm">
					<span className="font-medium text-dark-blue">Watermark:</span>
					<span className="ml-2 text-gray">
						{features.hasWatermark ? 'Yes' : 'No'}
					</span>
				</div>

				{features.canUseAdvancedFeatures && (
					<div className="flex items-center text-sm text-textGreen">
						<svg
							className="mr-2 h-5 w-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M5 13l4 4L19 7"
							/>
						</svg>
						Advanced Features
					</div>
				)}

				{features.canUseAISuggestions && (
					<div className="flex items-center text-sm text-green-600">
						<svg
							className="mr-2 h-5 w-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M5 13l4 4L19 7"
							/>
						</svg>
						Advanced Formatting
					</div>
				 )} 

				{features.canUseTemplates && (
					<div className="flex items-center text-sm text-textGreen">
						<svg
							className="mr-2 h-5 w-5"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M5 13l4 4L19 7"
							/>
						</svg>
						Structured Guides
					</div>
				)}
			</div>

		<button
			onClick={handleSelect}
			disabled={isLoading || !priceId || isExactMatch}
			className={`w-full h-[52px] rounded-xl px-4 py-2 font-semibold transition-colors ${
				isExactMatch
					? 'bg-lightGreen text-textGreen border-2 border-textGreen cursor-not-allowed'
					: isCurrentPlan
					? 'bg-orange text-white hover:opacity-90'
					: isLoading
					? 'bg-lightGrey text-gray cursor-wait'
					: 'bg-gradient-to-r from-orange to-purple text-white hover:opacity-90'
			}`}
		>
			{isLoading
				? 'Processing...'
				: isExactMatch
				? '✓ Current Plan'
				: isCurrentPlan
				? 'Change Billing'
				: 'Select Plan'}
		</button>
		</div>
	);
};

export default PlanCard;

